import Dexie, { Table } from 'dexie';
import { Warband, ModelTemplate, ItemTemplate } from '../types';

// Define the save data structure
export type SaveData = {
  warband: Warband;
  models: any[]; // We'll keep this flexible for now
  items: any[]; // We'll keep this flexible for now
  locations: any[]; // We'll keep this flexible for now
  modelTemplates: ModelTemplate[];
  itemTemplates: ItemTemplate[];
};

// Profile data stored in IndexedDB
export interface ProfileData {
  id: string; // Primary key - slugified profile name
  profileName: string;
  data: SaveData;
  createdAt: Date;
  updatedAt: Date;
  lastAccessedAt: Date;
}

// Dexie database class
export class TrenchCrusadeDB extends Dexie {
  profiles!: Table<ProfileData>;

  constructor() {
    super('TrenchCrusadeDB');

    this.version(1).stores({
      profiles: 'id, profileName, createdAt, updatedAt, lastAccessedAt'
    });
  }
}

// Create and export the database instance
export const db = new TrenchCrusadeDB();

// Helper function to slugify profile names for IDs
export function slugify(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^\w\s-]/g, '') // Remove special characters
    .replace(/[\s_-]+/g, '-') // Replace spaces and underscores with hyphens
    .replace(/^-+|-+$/g, ''); // Remove leading/trailing hyphens
}

// Local storage adapter class
export class LocalAdapter {
  private db: TrenchCrusadeDB;

  constructor() {
    this.db = db;
  }

  // Initialize the database
  async init(): Promise<void> {
    try {
      await this.db.open();
      console.log('Trench Crusade database initialized');
    } catch (error) {
      console.error('Failed to initialize database:', error);
      throw error;
    }
  }

  // List all profiles
  async listProfiles(): Promise<{id: string, name: string, updatedAt: Date, lastAccessedAt: Date}[]> {
    try {
      const profiles = await this.db.profiles
        .orderBy('lastAccessedAt')
        .reverse()
        .toArray();

      return profiles.map(profile => ({
        id: profile.id,
        name: profile.profileName,
        updatedAt: profile.updatedAt,
        lastAccessedAt: profile.lastAccessedAt
      }));
    } catch (error) {
      console.error('Failed to list profiles:', error);
      return [];
    }
  }

  // Load a specific profile
  async loadProfile(id: string): Promise<SaveData | null> {
    try {
      const profile = await this.db.profiles.get(id);
      if (!profile) return null;

      // Update last accessed time
      await this.db.profiles.update(id, { lastAccessedAt: new Date() });

      return profile.data;
    } catch (error) {
      console.error('Failed to load profile:', error);
      return null;
    }
  }

  // Save a profile (create or update)
  async saveProfile(id: string, data: SaveData, profileName?: string): Promise<void> {
    try {
      const now = new Date();
      const existingProfile = await this.db.profiles.get(id);

      if (existingProfile) {
        // Update existing profile
        await this.db.profiles.update(id, {
          data,
          updatedAt: now,
          lastAccessedAt: now
        });
      } else {
        // Create new profile
        await this.db.profiles.add({
          id,
          profileName: profileName || id,
          data,
          createdAt: now,
          updatedAt: now,
          lastAccessedAt: now
        });
      }
    } catch (error) {
      console.error('Failed to save profile:', error);
      throw error;
    }
  }

  // Delete a profile
  async deleteProfile(id: string): Promise<void> {
    try {
      await this.db.profiles.delete(id);
    } catch (error) {
      console.error('Failed to delete profile:', error);
      throw error;
    }
  }

  // Rename a profile
  async renameProfile(id: string, newName: string): Promise<void> {
    try {
      const newId = slugify(newName);
      const profile = await this.db.profiles.get(id);

      if (!profile) {
        throw new Error('Profile not found');
      }

      // Check if new ID already exists
      if (newId !== id) {
        const existingProfile = await this.db.profiles.get(newId);
        if (existingProfile) {
          throw new Error('A profile with this name already exists');
        }
      }

      // Update the profile
      await this.db.profiles.update(id, {
        id: newId,
        profileName: newName,
        updatedAt: new Date()
      });

      // If ID changed, we need to recreate the record
      if (newId !== id) {
        await this.db.profiles.delete(id);
      }
    } catch (error) {
      console.error('Failed to rename profile:', error);
      throw error;
    }
  }

  // Export profile as JSON download
  async exportProfile(id: string): Promise<void> {
    try {
      const profile = await this.db.profiles.get(id);
      if (!profile) {
        throw new Error('Profile not found');
      }

      const exportData = {
        ...profile.data,
        profileName: profile.profileName,
        exportedAt: new Date().toISOString()
      };

      const blob = new Blob([JSON.stringify(exportData, null, 2)], {
        type: 'application/json'
      });

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${profile.profileName.replace(/[^a-zA-Z0-9]/g, '_')}.json`;
      a.click();

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export profile:', error);
      throw error;
    }
  }

  // Import profile from JSON file
  async importProfile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = async (event) => {
        try {
          const result = event.target?.result;
          if (typeof result !== 'string') {
            throw new Error('Invalid file content');
          }

          const importData = JSON.parse(result);

          // Extract profile data
          const profileName = importData.profileName || 'Imported Profile';
          const profileId = slugify(profileName);

          // Check if profile already exists
          const existingProfile = await this.db.profiles.get(profileId);
          if (existingProfile) {
            // Generate unique ID
            const uniqueId = `${profileId}_${Date.now()}`;
            const data: SaveData = {
              warband: importData.warband,
              models: importData.models || [],
              items: importData.items || [],
              locations: importData.locations || [],
              modelTemplates: importData.modelTemplates || [],
              itemTemplates: importData.itemTemplates || []
            };

            await this.saveProfile(uniqueId, data, `${profileName} (Imported)`);
            resolve(uniqueId);
          } else {
            const data: SaveData = {
              warband: importData.warband,
              models: importData.models || [],
              items: importData.items || [],
              locations: importData.locations || [],
              modelTemplates: importData.modelTemplates || [],
              itemTemplates: importData.itemTemplates || []
            };

            await this.saveProfile(profileId, data, profileName);
            resolve(profileId);
          }
        } catch (error) {
          console.error('Failed to import profile:', error);
          reject(error);
        }
      };

      reader.onerror = () => {
        reject(new Error('Failed to read file'));
      };

      reader.readAsText(file);
    });
  }
}

// Export a singleton instance
export const localAdapter = new LocalAdapter();

